import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { cn } from "@/lib/utils.ts";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { AlertTriangle, Info, Zap } from "lucide-react";
import { Sparkle } from "@/components/icons/customIcons.tsx";

type InfoBannerComponentData = Extract<ArtifactComponent, { type: "infoBanner" }>;

const Icon = ({ icon }: { icon: InfoBannerComponentData["icon"] }) => {
  switch (icon) {
    case "sparkle":
      return <Sparkle />;
    case "zap":
      return <Zap />;
    case "alertTriangle":
      return <AlertTriangle />;
    case "info":
    default:
      return <Info />;
  }
};

const severityStyles: Record<NonNullable<InfoBannerComponentData["severity"]>, string> = {
  info: "bg-primary/5",
  warning: "bg-amber-100",
  error: "bg-destructive/10",
};

export const InfoBannerBlock = ({ component }: { component: InfoBannerComponentData }) => {
  const bg = severityStyles[component.severity ?? "info"];

  // Title-only compact strip — used by emailDraft's external-recipient
  // warning. Matches the legacy yellow strip rather than the multi-line alert
  // layout. `ArtifactView` makes it span the card by cancelling its own gutter
  // with `-mx-5`, so the strip restates that gutter to put its text back on the
  // column every other line is written against — otherwise the one line the
  // reader is meant to notice is the one hanging off to the left. Inside the
  // gutter already, it needs only its own breathing room.
  if (!component.body) {
    return (
      <div className={cn("py-2 text-sm", component.fullBleed ? "px-5" : "px-2", bg)}>
        {component.title}
      </div>
    );
  }
  return (
    <Alert className={cn("[&_[data-slot=alert-description]]:text-foreground", bg)}>
      <Icon icon={component.icon} />
      <AlertTitle>{component.title}</AlertTitle>
      <AlertDescription>{component.body}</AlertDescription>
    </Alert>
  );
};
